import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SEO } from "@/components/seo";
import { 
  TrendingUp, Bot, LineChart, Zap, Shield, BarChart3, Clock, 
  ArrowRight, Newspaper, Bell, Wallet, PieChart, FileText,
  Users, Lock, Cpu, Globe, CheckCircle2
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";

const mainFeatures = [
  {
    icon: Bot,
    title: "No-Code Rule Builder",
    description: "Create sophisticated trading algorithms without writing a single line of code. Our visual builder makes it easy to define conditions, triggers, and actions.",
    highlights: ["Visual drag-and-drop interface", "Pre-built condition templates", "Real-time preview"],
  },
  {
    icon: LineChart,
    title: "Multi-Asset Trading",
    description: "Trade across multiple asset classes from one unified platform. Stocks, options, ETFs, and cryptocurrencies all in one place.",
    highlights: ["Stocks & ETFs", "Options chains", "Cryptocurrency markets"],
  },
  {
    icon: Zap,
    title: "Event-Driven Automation",
    description: "React to market-moving events in real-time. Connect to news feeds, social media, and price alerts to trigger automated trades.",
    highlights: ["News sentiment analysis", "Social media monitoring", "Custom webhooks"],
  },
  {
    icon: Shield,
    title: "Advanced Risk Controls",
    description: "Protect your portfolio with built-in safety mechanisms. Set position limits, daily trade caps, and use paper trading to test strategies.",
    highlights: ["Position size limits", "Daily trade restrictions", "Paper trading mode"],
  },
];

const additionalFeatures = [
  { icon: BarChart3, title: "Portfolio Analytics", description: "Real-time P&L tracking and performance metrics" },
  { icon: Clock, title: "24/7 Execution", description: "Your algorithms run continuously without interruption" },
  { icon: Newspaper, title: "News Integration", description: "Connect to major news sources for instant reactions" },
  { icon: Bell, title: "Alert System", description: "Get notified when rules trigger or positions change" },
  { icon: Wallet, title: "Multi-Broker Support", description: "Connect to your preferred brokerage account" },
  { icon: PieChart, title: "Allocation Tools", description: "Manage portfolio allocation and rebalancing" },
  { icon: FileText, title: "Trade History", description: "Complete audit trail of all executed trades" },
  { icon: Users, title: "Account Types", description: "Individual, Joint, IRA, Entity, and Margin accounts" },
  { icon: Lock, title: "Bank-Grade Security", description: "Enterprise encryption and secure authentication" },
  { icon: Cpu, title: "API Access", description: "Programmatic access for advanced integrations" },
  { icon: Globe, title: "Global Markets", description: "Access to US and international exchanges" },
  { icon: CheckCircle2, title: "KYC Verification", description: "Streamlined identity verification process" },
];

const accountTypes = [
  {
    type: "Individual",
    description: "Standard taxable brokerage account for personal trading",
    features: ["Full trading capabilities", "Tax documentation", "Margin eligible"],
  },
  {
    type: "Joint",
    description: "Shared account with a spouse or partner",
    features: ["Joint ownership", "Survivorship rights", "Combined access"],
  },
  {
    type: "IRA",
    description: "Tax-advantaged retirement accounts",
    features: ["Traditional & Roth options", "Tax benefits", "Retirement planning"],
  },
  {
    type: "Entity",
    description: "For businesses, trusts, and organizations",
    features: ["LLC, Corp, Trust support", "Business documentation", "Multi-user access"],
  },
  {
    type: "Margin",
    description: "Leverage your buying power with margin trading",
    features: ["Increased buying power", "Short selling", "Options strategies"],
  },
];

export default function FeaturesPage() {
  const { isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Features - TradePro | Trading Automation Capabilities"
        description="Explore TradePro's powerful features: no-code rule builder, multi-asset trading, real-time data, paper trading, and professional risk controls."
      />
      <header className="fixed top-0 left-0 right-0 z-50 border-b bg-background/95 backdrop-blur">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link href="/" data-testid="link-home-logo">
            <div className="flex items-center gap-2 cursor-pointer">
              <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold">TradePro</span>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-6" data-testid="nav-main">
            <Link href="/features" className="text-sm text-foreground font-medium" data-testid="link-nav-features">Features</Link>
            <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="link-nav-pricing">Pricing</Link>
            <Link href="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="link-nav-about">About</Link>
          </nav>
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <Link href="/dashboard">
                <Button data-testid="button-go-to-dashboard">Go to Dashboard</Button>
              </Link>
            ) : (
              <>
                <Button variant="ghost" data-testid="button-login" onClick={() => setLocation("/login")}>Log in</Button>
                <Button data-testid="button-get-started" onClick={() => setLocation("/login")}>Get Started</Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="pt-24 pb-20">
        <section className="px-4 mb-16">
          <div className="container mx-auto text-center max-w-3xl">
            <Badge variant="secondary" className="mb-4">Platform Features</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Everything You Need to Trade Smarter</h1>
            <p className="text-xl text-muted-foreground">
              Powerful tools designed for traders of all levels. No coding required.
            </p>
          </div>
        </section>

        <section className="px-4 mb-20">
          <div className="container mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-8">
              {mainFeatures.map((feature) => (
                <Card key={feature.title} className="hover-elevate" data-testid={`feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}>
                  <CardHeader>
                    <div className="h-14 w-14 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <feature.icon className="h-7 w-7 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                    <CardDescription className="text-base">{feature.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {feature.highlights.map((highlight) => (
                        <li key={highlight} className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                          <span className="text-sm">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-20 bg-muted/30">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">More Powerful Features</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                A comprehensive suite of tools to support your trading journey
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {additionalFeatures.map((feature) => (
                <div key={feature.title} className="text-center p-4" data-testid={`additional-feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center mx-auto mb-3">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-1">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-20">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Flexible Account Types</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Choose the account type that fits your trading needs
              </p>
            </div>
            <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
              {accountTypes.map((account) => (
                <Card key={account.type} className="text-center" data-testid={`account-type-${account.type.toLowerCase()}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{account.type}</CardTitle>
                    <CardDescription className="text-xs">{account.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {account.features.map((f) => (
                        <li key={f}>{f}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 px-4 bg-muted/30">
          <div className="container mx-auto text-center max-w-2xl">
            <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Open an account today and start automating your trading strategies.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/open-account">
                <Button size="lg" data-testid="button-open-account">
                  Open New Account
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link href="/pricing">
                <Button size="lg" variant="outline" data-testid="button-view-pricing">
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t py-8 px-4">
        <div className="container mx-auto text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} TradePro. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
